import { z } from 'zod';
import { isUsPhoneNumber } from '@/utils/isUsPhoneNumber';

/* ================= COMPANY INFO (STEP 0) ================= */
export const companyInfoSchema = z.object({
  business_legal_name: z.string().min(1, 'Required'),
  website_url: z.string().min(1, 'Required'),
  primary_contact_name: z.string().min(1, 'Required'),
  primary_contact_email: z.string().email('Invalid email'),
  primary_contact_phone: z.string().min(6, 'Required')
});

export const companyAddressSchema = z.object({
  street_address: z.string().min(1, 'Required'),
  city: z.string().min(1, 'Required'),
  postal_zip_code: z.string().min(1, 'Required'),
  state_province_region: z.string().min(1, 'Required'),
  country: z.string().min(1, 'Required')
});

/* ================= INTEGRATIONS (onboarding step index 8) ================= */
export const integrationSchema = z.object({
  available_integration_ids: z.array(z.any()).min(1, 'Integration is required')
});

/* ================= OPERATOR DETAILS ================= */
const workingHoursSchema = z
  .record(
    z.object({
      open: z.boolean().optional(),
      from_time: z.string().optional(),
      to_time: z.string().optional()
    })
  )
  .refine((val) => Object.values(val || {}).some((d: any) => d?.open), {
    message: 'At least one working day must be enabled'
  });

const operatorSchema = z
  .object({
    name: z.string().min(1, 'Required'),
    email: z.string().email('Invalid email'),
    phone: z.string().min(6, 'Required'),
    sms_consent: z.boolean().optional(),
    working_hours: workingHoursSchema,
    extension: z.any().optional(),
    sip_uri: z.any().optional()
  })
  .superRefine((data, ctx) => {
    if (isUsPhoneNumber(data.phone) && data.sms_consent !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'SMS consent is required',
        path: ['sms_consent']
      });
    }
  });

/* ================= INTERNAL PROCESS ================= */
export const internalProcessSchema = z.object({
  business_plan_id: z.string().min(1, 'Required'),
  voice_engine_id: z.string().min(1, 'Required'),
  ticket_reference: z.string().nullish(),

  coupon_code: z.string().nullish(),
  stripe_customer_id: z.string().nullish(),

  telephony_provider_ids: z.array(z.string()).optional(),

  interoffice_notes: z
    .array(
      z.object({
        note: z.string().optional(),
        timestamp: z.string().optional()
      })
    )
    .optional()
});

const locationDetailsSchema = z
  .object({
    count: z.coerce.string().min(1, 'Required'),

    location_name: z.string().min(1, 'Required'),

    physical_location_exists: z.boolean(),

    // optional by default
    street: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    zip: z.string().optional(),
    country: z.string().optional(),
    state: z.string().optional()
  })
  .superRefine((data, ctx) => {
    if (data.physical_location_exists) {
      if (!data.street) {
        ctx.addIssue({ path: ['street'], message: 'Required', code: 'custom' });
      }
      if (!data.city) {
        ctx.addIssue({ path: ['city'], message: 'Required', code: 'custom' });
      }
      if (!data.zip) {
        ctx.addIssue({ path: ['zip'], message: 'Required', code: 'custom' });
      }
      if (!data.country) {
        ctx.addIssue({ path: ['country'], message: 'Required', code: 'custom' });
      }
      if (!data.state) {
        ctx.addIssue({ path: ['state'], message: 'Required', code: 'custom' });
      }
    }
  });

/* ================= MAIN SCHEMA ================= */
export const onboardingSchema = companyInfoSchema.merge(integrationSchema).extend({
  location_details: locationDetailsSchema,
  address: companyAddressSchema.extend({
    time_zone: z.string().min(1, 'Required')
  }),

  operator_details: z.array(operatorSchema).min(1, 'At least one operator required'),
  ...internalProcessSchema.shape
});

/* ================= PUBLIC SCHEMA (STAGE 1/2) ================= */
export const publicOnboardingSchema = integrationSchema.extend({
  location_details: locationDetailsSchema,
  address: z.object({
    time_zone: z.string().min(1, 'Required')
  }),

  operator_details: z.array(operatorSchema).min(1, 'At least one operator required')
});
